import React, { useContext, useEffect, useRef, useState } from 'react';
import styles from './CampaignLaunch.module.css';
import { Link } from 'react-router-dom'
import { BASE_URL_API } from '../../../References/URLs';
import AuthContext from '../../../store/auth-context';
import ActiveStoreContext from '../../../store/ActiveStoreContext';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import { FaSpinner } from 'react-icons/fa';
import CampaignCostCalculator from './CampaignCostCalculator';
import BroadcastJobProgress from './BroadcastJobProgress';
import StepHeader from './shared/StepHeader';
import { formatUTCToIST, deriveSendState } from './shared/campaignStatus';

// One-line revert switch: flip to false + redeploy to fall back to the
// old per-batch sendPromoComViaWaBulk loop below (kept fully intact).
const USE_BROADCAST_SEND = true;

// Polling cadence/timeout for the broadcast job status check.
const BROADCAST_POLL_INTERVAL_MS = 4000;
const BROADCAST_POLL_MAX_ATTEMPTS = 90; // ~6 minutes at 4s/attempt, once firing
const BROADCAST_TERMINAL_STATUSES = ['completed', 'cancelled', 'failed'];

// --- Schedule-for-later, absorbed from the old CampaignScheduler.js step ---
// Removed as its own wizard step per real user testing: a standalone step
// forced an extra click, and worse, cancelling a scheduled campaign left no
// way back to relaunch/reschedule without manually switching tabs. Folded
// in here so the whole launch/schedule/cancel/relaunch cycle lives on one
// screen, always re-derived from getCampaignDetails (never cached locally).

// datetime-local gives back "YYYY-MM-DDTHH:mm" (or ...:ss if the input has
// a seconds step, which this one doesn't). Confirmed exact backend format
// is "Y-m-d H:i:s" — built via plain string manipulation on the browser's
// own value rather than round-tripping through a Date object, since this
// is India-only (no timezone selector) and the browser's local wall-clock
// time IS the IST time the user picked — a Date-based reformat risks a UTC
// conversion nobody asked for.
const formatScheduledAtIST = (datetimeLocalValue) => {
    if (!datetimeLocalValue) return null;
    const [datePart, timePart] = datetimeLocalValue.split('T');
    if (!datePart || !timePart) return null;
    const normalizedTime = timePart.length === 5 ? `${timePart}:00` : timePart;
    return `${datePart} ${normalizedTime}`;
};

// For the picker's `min` attribute — same "YYYY-MM-DDTHH:mm" shape
// datetime-local expects, built from the browser's own local (IST) clock.
const nowAsDatetimeLocal = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

// Cost = audience size × per-recipient rate × currency-conversion buffer ×
// GST. Extracted once so the amount quoted to the user (amountRequired,
// ceiled for headroom) and the amount actually charged (deductWalletBalance,
// precise) are computed from the same formula and can never drift apart —
// they previously used different multipliers (1.02 here vs a stray 1.03 in
// deductWalletBalance). The toFixed(2)-then-Number round-trip before Math.ceil
// guards against float noise pushing a value that should land exactly on an
// integer (e.g. 12.000000000000002) up into the next one.
const calculateRawCampaignCost = (audienceCount) => (audienceCount || 0) * 0.76 * 1.02 * 1.18;
const calculateCampaignCost = (audienceCount) => Math.ceil(Number(calculateRawCampaignCost(audienceCount).toFixed(2)));

// Currency display — both walletBalance and walletOnHold can otherwise show
// raw binary floating-point noise (e.g. "₹4.619999999999891" from a plain
// current - available subtraction).
const fmtAmount = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CampaignLaunch = ({ data, setData, getCampaignDetails }) => {
    const { campaignIdentifier } = useParams()
    const authCtx = useContext(AuthContext);
    const vendorIdentifier = JSON.parse(authCtx?.loginData)?.partnersData?.vendorIdentifier
    const asCtx = useContext(ActiveStoreContext);
    const activeStore = (() => { try { return (asCtx?.activeStore && asCtx.activeStore.trim() !== "") ? JSON.parse(asCtx.activeStore) : null; } catch { return null; } })();
    const [isCurrentlyLaunching, setIsCurrentlyLaunching] = useState(false)
    const nav = useNavigate()
    const [isLoadingAudience, setIsLoadingAudience] = useState(false)
    const [templateName, setTemplateName] = useState('valentines_week_special_get_a_voucher');
    // Was previously seeded with 3 hard-coded fake "Nav" recipients as
    // placeholder/test data — that's what let a stale "3 recipients" count
    // render even when getCampaignAudienceData came back with status:"failed"
    // (the failure branch below never reset this). Starts empty now; only
    // ever populated by a successful fetch.
    const [audienceData, setAudienceData] = useState([]);
    const [analysisData, setAnalysisData] = useState({});
    const [walletBalance, setWalletBalance] = useState(0)
    // currentBalance - availableBalance — how much is reserved by holds
    // across this store's other pending campaigns, shown alongside the
    // spendable figure so the two numbers aren't just one opaque total.
    const [walletOnHold, setWalletOnHold] = useState(0)
    const [isPaymentRequired, setIsPaymentRequired] = useState(false)
    const [amountRequired, setAmountRequired] = useState(1)
    const [isLoadingWalletBalance, setIsLoadingWalletBalance] = useState(false)

    // Broadcast-send state (USE_BROADCAST_SEND path only).
    const [broadcastJob, setBroadcastJob] = useState(null) // { status, submitted, delivered, read, failed, total }
    const [broadcastPollTimedOut, setBroadcastPollTimedOut] = useState(false)
    // SEND_AMBIGUOUS: hold is still active and it's genuinely unknown whether
    // the send happened — kept separate from isCurrentlyLaunching so it can
    // permanently disable the Launch button (no retry: a retry would just
    // fail with ALREADY_LAUNCHED against the still-active hold) without the
    // misleading "Being Launched..." label.
    const [isSendAmbiguous, setIsSendAmbiguous] = useState(false)
    const pollTimerRef = useRef(null)
    const pollAttemptsRef = useRef(0)

    // Schedule-for-later state (absorbed from CampaignScheduler.js).
    const [showSchedulePicker, setShowSchedulePicker] = useState(false)
    const [scheduledAtInput, setScheduledAtInput] = useState('')
    const [isScheduling, setIsScheduling] = useState(false)
    const [isCancelling, setIsCancelling] = useState(false)
    const schedulePollTimerRef = useRef(null)

    // holdStatus/scheduledAt/broadcastJobId reflect the LATEST hold row for
    // this campaign — a campaign can legitimately cycle through
    // schedule -> cancel -> relaunch, so this is always re-derived from
    // whatever getCampaignDetails most recently returned, never cached or
    // inferred client-side across re-renders.
    const holdStatus = data?.holdStatus
    const scheduledAt = data?.scheduledAt
    const broadcastJobId = data?.broadcastJobId
    const { isScheduledNotFired, isFiring } = deriveSendState({ holdStatus, scheduledAt, broadcastJobId })
    // Covers "no hold ever existed", RELEASED/EXPIRED, and (defensively,
    // via isSendAmbiguous below) a HELD hold with neither a schedule nor a
    // job yet — never offer Launch/Schedule again into that last case.
    const isAvailable = !isScheduledNotFired && !isFiring

    // Once the campaign has moved into a definitively-known state (actually
    // scheduled, or actually firing), any earlier SEND_AMBIGUOUS warning
    // from this screen is resolved — don't leave Launch/Schedule
    // permanently disabled behind a stale ambiguity flag.
    useEffect(() => {
        if (isFiring || isScheduledNotFired) {
            setIsSendAmbiguous(false)
        }
    }, [isFiring, isScheduledNotFired])

    useEffect(() => {
        setAmountRequired(calculateCampaignCost(audienceData?.length))
    }, [])

    const getCampaignAudienceData = async () => {
        setIsLoadingAudience(true)
        const response = await fetch(BASE_URL_API + "getCampaignAudienceData",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    campaignIdentifier: campaignIdentifier,
                    storeIdentifier: activeStore?.storeIdentifier
                })
            })

        if (!response.ok) {
            toast.error(`Couldn't load this campaign's audience.`)
            setAudienceData([])
        } else {
            const respo = await response.json()
            if (respo?.status === "success") {
                setAudienceData(respo?.response || [])
            } else {
                if (respo?.message === "tokenExpired") {
                    authCtx.logout()
                } else {
                    toast.error(respo?.message || `Couldn't load this campaign's audience.`)
                }
                // Don't leave a previous (or default) audienceData value
                // standing when the fetch failed — an empty audience also
                // correctly blocks Launch Now via the audienceData.length < 1
                // guard in sendAllPromoComViaWa.
                setAudienceData([])
            }
        }
        setIsLoadingAudience(false)
    }

    useEffect(() => {
        if (campaignIdentifier) {
            getCampaignAudienceData()
        }
    }, [campaignIdentifier])

    const getStoreWalletBalance = async () => {
        setIsLoadingWalletBalance(true)
        const response = await fetch(BASE_URL_API + "getStoreWalletBalance",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore?.storeIdentifier
                })
            })

        if (!response.ok) {
            toast.error(`Couldn't load your wallet balance.`)
        } else {
            const respo = await response.json()
            if (respo?.status === "success") {
                // availableBalance (not currentBalance) is what actually
                // gates Launch/Schedule — currentBalance still includes
                // money already reserved by another pending campaign's
                // hold. The gap between the two is shown alongside it so
                // the user can see why available is lower than their total.
                const available = respo?.availableBalance ?? 0
                const current = respo?.currentBalance ?? available
                setWalletBalance(available)
                // Rounded to the nearest paisa here (not just at display) so a
                // near-zero float residue from the subtraction (e.g. when
                // current and available are meant to be equal) can't leave
                // walletOnHold as a stray 0.00000000002, which would wrongly
                // pass the `walletOnHold > 0` check below and show a bogus
                // "on hold" line.
                setWalletOnHold(Math.max(0, Math.round((current - available) * 100) / 100))
            } else {
                if (respo?.message === "tokenExpired") {
                    authCtx.logout()
                } else {
                    toast.error(respo?.message || `Couldn't load your wallet balance.`)
                }
                setWalletBalance(0)
                setWalletOnHold(0)
            }
        }
        setIsLoadingWalletBalance(false)
    }

    const deductWalletBalance = async () => {
        const response = await fetch(BASE_URL_API + "deductWalletBalance",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    campaignIdentifier: campaignIdentifier,
                    vendorIdentifier: vendorIdentifier,
                    storeIdentifier: activeStore?.storeIdentifier,
                    amount: calculateRawCampaignCost(audienceData?.length).toFixed(2)
                })
            })

        if (!response.ok) {
            toast.error(`Couldn't update your wallet balance.`)
        } else {
            const respo = await response.json()
            if (respo?.status === "success") {
                getStoreWalletBalance()
            } else {
                if (respo?.message === "tokenExpired") {
                    authCtx.logout()
                } else {
                    toast.error(respo?.message || `Couldn't update your wallet balance.`)
                }
            }
        }
    }


    // --- New broadcast-send path (USE_BROADCAST_SEND === true) ---------------

    // Drives the progress panel whenever holdStatus/broadcastJobId (the
    // single source of truth from getCampaignDetails) says a job is firing
    // — whether it got there via an immediate Launch Now or a schedule that
    // already fired. Runs on mount into an already-firing state too (e.g.
    // navigating back to this step after the fact), not just right after a
    // fresh launch click — the initial check is silent (no toast) since
    // discovering an already-terminal job on arrival isn't a new event.
    useEffect(() => {
        if (!isFiring) return
        let stopped = false
        pollAttemptsRef.current = 0
        setBroadcastPollTimedOut(false)

        const check = async (isInitialCheck) => {
            try {
                const response = await fetch(BASE_URL_API + "getBroadcastCampaignStatus", {
                    method: "POST",
                    body: JSON.stringify({
                        token: authCtx.token,
                        campaignIdentifier: campaignIdentifier
                    })
                })

                if (!response.ok) {
                    // Deliberately silent here (no toast) — a single failed poll
                    // just retries below via the normal cadence; nagging the user
                    // on every transient network blip during a background poll
                    // would be worse than the eventual broadcastPollTimedOut state.
                } else {
                    const respo = await response.json()
                    if (respo?.status === "success") {
                        const job = respo?.response || {}
                        setBroadcastJob(job)

                        if (BROADCAST_TERMINAL_STATUSES.includes(job?.status)) {
                            // Settlement (charge on completed / release on
                            // cancelled-or-failed) happens atomically,
                            // server-side, inside getBroadcastCampaignStatus
                            // itself — this getStoreWalletBalance() call just
                            // refreshes the displayed balance afterwards.
                            getStoreWalletBalance()
                            if (!isInitialCheck) {
                                if (job.status === 'completed') {
                                    toast(`Campaign completed!`)
                                } else if (job.status === 'cancelled') {
                                    toast(`Campaign was cancelled.`)
                                } else {
                                    toast.error(`Campaign failed to send. Please try again.`)
                                }
                            }
                            return
                        }
                    } else if (respo?.message === "tokenExpired") {
                        authCtx.logout()
                        return
                    }
                    // Any other non-success, non-terminal response also just
                    // falls through to the retry below, same reasoning as above.
                }
            } catch (error) {
                console.log("getBroadcastCampaignStatus poll failed", error)
            }

            pollAttemptsRef.current += 1
            if (pollAttemptsRef.current >= BROADCAST_POLL_MAX_ATTEMPTS) {
                setBroadcastPollTimedOut(true)
                return
            }

            if (!stopped) {
                pollTimerRef.current = setTimeout(() => check(false), BROADCAST_POLL_INTERVAL_MS)
            }
        }

        check(true)

        return () => {
            stopped = true
            if (pollTimerRef.current) {
                clearTimeout(pollTimerRef.current)
                pollTimerRef.current = null
            }
        }
    }, [isFiring, campaignIdentifier])

    const launchViaBroadcast = async () => {
        try {
            const response = await fetch(BASE_URL_API + "launchBroadcastCampaign", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    campaignIdentifier: campaignIdentifier
                })
            })

            if (!response.ok) {
                setIsCurrentlyLaunching(false)
                toast.error(`Couldn't launch the campaign. Please try again.`)
                return
            }

            const respo = await response.json()
            if (respo?.status === "success") {
                // launchBroadcastCampaign's idempotencyKey is deterministic per
                // campaignIdentifier, so a retry/double-click here safely rejoins
                // the same job server-side rather than double-sending — the
                // isCurrentlyLaunching disable on the button below is just UX,
                // not a correctness guard. Re-fetch campaign details (rather
                // than trusting this response's own payload) so holdStatus/
                // broadcastJobId — the source of truth this screen renders
                // off of — actually reflects the new hold; that flips the
                // screen into the firing view, which owns its own poll.
                await getCampaignDetails?.()
                setIsCurrentlyLaunching(false)
            } else {
                if (respo?.message === "tokenExpired") {
                    authCtx.logout()
                } else {
                    // wa_placeHold() rejections surface via respo.errorCode — 8
                    // values total. ALREADY_LAUNCHED/INSUFFICIENT_FUNDS/LOCK_BUSY/
                    // DB_ERROR get their own copy; SEND_AMBIGUOUS gets distinct
                    // handling below (not a normal failure — see isSendAmbiguous).
                    // Everything else (MISSING_CAMPAIGN_IDENTIFIER,
                    // CAMPAIGN_NOT_READY, VENDOR_NOT_RESOLVED, SEND_REJECTED) falls
                    // through to the fallback, which just shows respo.message
                    // as-is — those are already human-readable and stable, no
                    // per-code copy needed. CAMPAIGN_NOT_READY in particular is a
                    // deliberately coarse code covering several underlying issues
                    // (missing campaign/audience/template/etc.) — fine to leave
                    // message-driven until the backend splits it further.
                    if (respo?.errorCode === "ALREADY_LAUNCHED") {
                        toast.error(`This campaign has already been sent — it can't be launched again.`)
                    } else if (respo?.errorCode === "INSUFFICIENT_FUNDS") {
                        toast.error(`Your wallet balance can't cover this campaign right now. Please add funds and try again.`)
                        getStoreWalletBalance()
                    } else if (respo?.errorCode === "LOCK_BUSY") {
                        toast.error(`This campaign is already being processed — please try again in a moment.`)
                    } else if (respo?.errorCode === "DB_ERROR") {
                        toast.error(`Something went wrong. Please try again.`)
                    } else if (respo?.errorCode === "SEND_AMBIGUOUS") {
                        // Wallet hold is still active and it's unknown whether the
                        // message actually went out — do NOT invite a retry, it
                        // would just collide with the existing hold as
                        // ALREADY_LAUNCHED. isSendAmbiguous keeps Launch Now
                        // disabled independently of isCurrentlyLaunching below.
                        toast.warn(`We're verifying whether this campaign went out — your balance is temporarily on hold. Check back in a few minutes, or contact support if this doesn't resolve.`)
                        setIsSendAmbiguous(true)
                    } else {
                        toast.error(respo?.message || `Couldn't launch the campaign. Please try again.`)
                    }
                }
                setIsCurrentlyLaunching(false)
            }
        } catch (error) {
            console.log("launchBroadcastCampaign failed", error)
            setIsCurrentlyLaunching(false)
            toast.error(`Couldn't launch the campaign. Please try again.`)
        }
    }

    // --- Schedule-for-later (absorbed from CampaignScheduler.js) -------------

    // While holdStatus is HELD with a scheduledAt and no broadcastJobId yet,
    // poll for the schedule firing (or being cancelled/changed elsewhere).
    // "Still scheduled" responses just keep waiting indefinitely — a
    // campaign scheduled hours out shouldn't hit a timeout purely for not
    // having fired yet, so there's no attempt budget here (unlike the
    // firing poll above, which does have one once a job actually exists).
    // Any change in the response — jobId present, or status no longer
    // "scheduled" — re-fetches campaign details instead of guessing the new
    // state locally, since getCampaignDetails is the single source of truth.
    useEffect(() => {
        if (!isScheduledNotFired) return
        let stopped = false

        const poll = async () => {
            try {
                const response = await fetch(BASE_URL_API + "getBroadcastCampaignScheduleStatus", {
                    method: "POST",
                    body: JSON.stringify({
                        token: authCtx.token,
                        campaignIdentifier
                    })
                })

                if (response.ok) {
                    const respo = await response.json()
                    if (respo?.status === "success") {
                        const info = respo?.response || {}
                        if (info?.jobId || info?.status !== 'scheduled') {
                            await getCampaignDetails?.()
                            return
                        }
                    } else if (respo?.message === "tokenExpired") {
                        authCtx.logout()
                        return
                    }
                    // Still scheduled (or an unrecognized shape) — keep polling.
                }
            } catch (error) {
                console.log("getBroadcastCampaignScheduleStatus poll failed", error)
            }

            if (!stopped) {
                schedulePollTimerRef.current = setTimeout(poll, BROADCAST_POLL_INTERVAL_MS)
            }
        }

        poll()

        return () => {
            stopped = true
            if (schedulePollTimerRef.current) {
                clearTimeout(schedulePollTimerRef.current)
                schedulePollTimerRef.current = null
            }
        }
    }, [isScheduledNotFired, campaignIdentifier])

    const scheduleCampaign = async () => {
        const scheduledAtIST = formatScheduledAtIST(scheduledAtInput)
        if (!scheduledAtIST) {
            toast.error(`Please pick a send time.`)
            return
        }

        setIsScheduling(true)
        try {
            const response = await fetch(BASE_URL_API + "scheduleBroadcastCampaign", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    campaignIdentifier,
                    scheduledAtIST
                })
            })

            if (!response.ok) {
                toast.error(`Couldn't schedule the campaign. Please try again.`)
                return
            }

            const respo = await response.json()
            if (respo?.status === "success") {
                toast(`Campaign scheduled!`)
                setShowSchedulePicker(false)
                setScheduledAtInput('')
                await getCampaignDetails?.()
                // Placing the hold immediately moves money from
                // available to on-hold — reflect that on screen right
                // away rather than waiting for the next poll/toast.
                getStoreWalletBalance()
            } else {
                if (respo?.message === "tokenExpired") {
                    authCtx.logout()
                } else if (respo?.errorCode === "ALREADY_LAUNCHED") {
                    toast.error(`This campaign has already been sent — it can't be scheduled.`)
                } else if (respo?.errorCode === "INSUFFICIENT_FUNDS") {
                    toast.error(`Your wallet balance can't cover this campaign right now. Please add funds and try again.`)
                    getStoreWalletBalance()
                } else if (respo?.errorCode === "LOCK_BUSY") {
                    toast.error(`This campaign is already being processed — please try again in a moment.`)
                } else if (respo?.errorCode === "DB_ERROR") {
                    toast.error(`Something went wrong. Please try again.`)
                } else {
                    toast.error(respo?.message || `Couldn't schedule the campaign. Please try again.`)
                }
            }
        } catch (error) {
            console.log("scheduleBroadcastCampaign failed", error)
            toast.error(`Couldn't schedule the campaign. Please try again.`)
        } finally {
            setIsScheduling(false)
        }
    }

    const cancelSchedule = async () => {
        setIsCancelling(true)
        try {
            const response = await fetch(BASE_URL_API + "cancelScheduledBroadcastCampaign", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    campaignIdentifier
                })
            })

            if (!response.ok) {
                toast.error(`Couldn't cancel the campaign. Please try again.`)
                return
            }

            const respo = await response.json()
            if (respo?.status === "success") {
                toast(`Campaign cancelled — your balance has been released.`)
                // Re-fetch (not an assumed local state flip) — this should
                // now come back with holdStatus RELEASED, which naturally
                // re-derives isAvailable and brings back both Launch Now and
                // Schedule. That round trip is the actual fix for the "no
                // way back after cancelling" bug.
                await getCampaignDetails?.()
                getStoreWalletBalance()
            } else {
                if (respo?.message === "tokenExpired") {
                    authCtx.logout()
                } else if (respo?.errorCode === "TOO_LATE_TO_CANCEL") {
                    // Not a generic error — the campaign already started
                    // processing. Re-check real state instead of guessing;
                    // it's very likely 'firing' now.
                    toast.error(`This campaign already started processing and can no longer be cancelled.`)
                    await getCampaignDetails?.()
                } else {
                    toast.error(respo?.message || `Couldn't cancel the campaign. Please try again.`)
                }
            }
        } catch (error) {
            console.log("cancelScheduledBroadcastCampaign failed", error)
            toast.error(`Couldn't cancel the campaign. Please try again.`)
        } finally {
            setIsCancelling(false)
        }
    }

    // --- Legacy per-batch send path (USE_BROADCAST_SEND === false) -----------
    // Kept fully intact and unused while the flag above is true.

    // Helper function to divide data into batches of a specified size
    const chunkArray = (arr, size) => {
        const result = [];
        for (let i = 0; i < arr.length; i += size) {
            result.push(arr.slice(i, i + size));
        }
        return result;
    };

    // Function to send messages in batches
    const sendBatch = async (batch, template) => {
        const startTime = Date.now(); // Start time for this batch
        const response = await fetch(BASE_URL_API + "sendPromoComViaWaBulk", {
            method: "POST",
            body: JSON.stringify({
                token: authCtx.token,
                phoneNumberData: batch,
                campaignIdentifier: campaignIdentifier,
                templateName: data?.templateName,
                templateIdentifier: data?.templateIdentifier
            })
        });

        const endTime = Date.now(); // End time for this batch
        const executionTime = endTime - startTime; // Calculate time taken for this batch

        if (!response.ok) {
            console.error("Server error in batch request.");
            return {
                batchData: batch.map(item => ({ ...item, status: "Failed" })),
                executionTime,
                serverErrorCount: 1,
            };
        } else {
            const data = await response.json();
            const batchData = data?.response.map((item, idx) => ({
                ...batch[idx],
                status: item.status === "success" ? "Sent" : "Failed",
                responseData: item.response || {}
            }));

            const successfullySent = batchData.filter(item => item.status === "Sent").length;
            const failedSent = batchData.filter(item => item.status === "Failed").length;

            return {
                batchData,
                executionTime,
                successfullySent,
                failedSent,
                serverErrorCount: 0,
            };
        }
    };

    // Function to analyze and log batch processing statistics
    const analyzeBatchProcess = (batches, batchResults, startTime) => {
        const totalExecutionTime = Date.now() - startTime;
        const totalBatches = batches.length;
        const totalData = audienceData.length;

        const successfullySent = batchResults.reduce((acc, res) => acc + res.successfullySent, 0);
        const failedSent = batchResults.reduce((acc, res) => acc + res.failedSent, 0);
        const serverErrorCount = batchResults.reduce((acc, res) => acc + res.serverErrorCount, 0);

        const averageBatchTime = batchResults.reduce((acc, res) => acc + res.executionTime, 0) / totalBatches;

        setAnalysisData({
            totalData,
            totalBatches,
            totalExecutionTime,
            successfullySent,
            failedSent,
            serverErrorCount,
            averageBatchTime,
        });

        console.log("Batch Analysis:", {
            totalData,
            totalBatches,
            totalExecutionTime,
            successfullySent,
            failedSent,
            serverErrorCount,
            averageBatchTime,
        });
    };

    // Function to handle the entire audience data in batches (legacy path)
    const sendAllPromoComViaWaLegacyBatchLoop = async () => {
        const startTime = Date.now();
        const batches = chunkArray(audienceData, 30);
        const batchResults = [];

        for (const batch of batches) {
            const result = await sendBatch(batch, templateName);
            batchResults.push(result);

            // Update the status in audienceData in real time
            setAudienceData(prevData =>
                prevData.map(item =>
                    result.batchData.find(res => res.phoneNumber === item.phoneNumber) || item
                )
            );
        }
        setIsCurrentlyLaunching(false)
        // Analyze the batch process once all batches are completed
        analyzeBatchProcess(batches, batchResults, startTime);
        deductWalletBalance()
        toast(`Campaign completed!`)
    };

    // Entry point wired to the "Launch Now" button. Affordability gate is
    // shared by both the broadcast and legacy paths; only the actual send
    // mechanism differs below.
    const sendAllPromoComViaWa = async () => {
        if (campaignIdentifier && campaignIdentifier.trim() !== "") {
            if (data?.templateName && data?.templateName?.trim() !== '') {
                // let's calculate balance
                if (audienceData?.length < 1) {
                    toast.error(`You must add an audience before launching.`)
                    nav(`/communications/campaign/${campaignIdentifier}/audience`)
                    return
                }
                let amtReq = calculateCampaignCost(audienceData?.length)
                setAmountRequired(amtReq)
                if (amtReq > walletBalance) {
                    setIsPaymentRequired(true)
                    return;
                } else {
                    setIsPaymentRequired(false)
                }

                setIsCurrentlyLaunching(true)

                if (USE_BROADCAST_SEND) {
                    await launchViaBroadcast()
                } else {
                    await sendAllPromoComViaWaLegacyBatchLoop()
                }

            } else {
                toast.error(`You must select a template before launching.`)
                nav(`/communications/campaign/${campaignIdentifier}/templates`)
            }
        } else {
            toast.error(`Campaign identifier can't be blank.`)
        }
    };

    useEffect(() => {
        getStoreWalletBalance()
    }, [])

    // Previously only the click handler checked template/audience prerequisites
    // (after the fact, via alert/toast) — the button itself looked identically
    // "active" whether or not the campaign was actually launchable. Now the
    // button's own enabled state requires both, same as the click handler.
    const hasTemplate = !!(data?.templateName && data?.templateName?.trim() !== '')
    const hasAudience = (audienceData?.length || 0) >= 1
    // isScheduling included here (and isCurrentlyLaunching included in the
    // Schedule button's own disabled checks below) so the two buttons can't
    // both be mid-submission at once — previously each only guarded against
    // its own in-flight request, so clicking Launch Now right after Schedule
    // Campaign (while that request was still in flight) would fire both.
    const isLaunchBlocked = isCurrentlyLaunching || isScheduling || isSendAmbiguous || isLoadingAudience || !hasTemplate || (!isLoadingAudience && !hasAudience)
    const launchLabel = isCurrentlyLaunching
        ? 'Being Launched...'
        : isSendAmbiguous
            ? 'Verifying send status...'
            : isLoadingAudience
                ? 'Loading Audience...'
                : !hasTemplate
                    ? 'Select a Template First'
                    : !hasAudience
                        ? 'Add an Audience First'
                        : 'Launch Now'

    return (
        <div className={styles.container}>
            {isPaymentRequired && <CampaignCostCalculator isOpen={isPaymentRequired} amountRequired={amountRequired} onClose={() => setIsPaymentRequired(false)} />}
            <StepHeader
                eyebrow="Step 4 of 5"
                title={data?.campaignName || 'Untitled Campaign'}
                subtitle={hasTemplate ? <span className="ccTag">{data?.templateName}</span> : null}
                right={
                    <div className={styles.launch}>
                        <div className={styles.balanceBlock}>
                            <div className={styles.availableTxt}>
                                Available Balance
                            </div>
                            <div className={styles.balanceValue}>
                                {fmtAmount(walletBalance)}
                            </div>
                            {walletOnHold > 0 && (
                                <div className={styles.balanceOnHold}>
                                    {fmtAmount(walletOnHold)} on hold across pending campaigns
                                </div>
                            )}
                        </div>
                        {/* Hidden entirely (not just disabled) once the campaign is
                            scheduled or firing — this button only makes sense in the
                            "available to send" state. */}
                        {isAvailable && (
                            <div className={styles.launchBtnWrapper}>
                                <button
                                    className={`${styles.launchBtn} ${isLaunchBlocked ? styles.launchBtnDisabled : styles.launchBtnActive}`}
                                    disabled={isLaunchBlocked}
                                    onClick={() => sendAllPromoComViaWa()}
                                >
                                    {(isCurrentlyLaunching || isLoadingAudience) && <FaSpinner className={styles.btnSpinner} />}
                                    {launchLabel}
                                </button>
                            </div>
                        )}
                    </div>
                }
            />
            <div className={styles.launchBody}>
                <div className={`${styles.launchBodyContainer} ${USE_BROADCAST_SEND ? styles.launchBodyContainerBroadcast : ''}`}>
                    <div className={styles.dataTableWrapper}>
                        {!USE_BROADCAST_SEND ?
                            <div className={styles.listWrapper}>
                                    <div className={`${styles.eachRow} ${styles.tableHeader}`}>
                                        <div className={styles.sn}>#</div>
                                        <div className={styles.clientName}>Client </div>
                                        <div className={styles.phoneNumber}>Phone</div>
                                        <div className={styles.gender}>Gender</div>
                                        <div className={styles.status} >Status</div>
                                    </div>
                                    {audienceData?.map((item, index) => (
                                        <div className={styles.eachRow} key={index}>
                                            <div className={styles.sn}>{index + 1}.</div>
                                            <div className={styles.clientName}>{item?.clientName}</div>
                                            <div className={styles.phoneNumber}>{item?.phoneNumber}</div>
                                            <div className={styles.gender}>{item?.gender}</div>
                                            <div className={styles.status} >{item?.status || "Pending"}</div>
                                        </div>
                                    ))}
                                </div>
                            : isFiring ?
                                // Broadcast pipeline only exposes aggregate counts, not
                                // per-recipient live status — per-recipient detail is
                                // still available afterwards via CampaignPerformance.js
                                // once that screen is updated to read the new pipeline.
                                <div>
                                    <BroadcastJobProgress
                                        audienceCount={audienceData?.length || 0}
                                        broadcastJob={broadcastJob}
                                        pollTimedOut={broadcastPollTimedOut}
                                    />
                                    {isSendAmbiguous && (
                                        <div className={styles.broadcastAmbiguous} style={{ marginTop: 14 }}>
                                            We're verifying whether this campaign went out — your balance is temporarily on hold. Check back in a few minutes, or contact support if this doesn't resolve.
                                        </div>
                                    )}
                                </div>
                            : isScheduledNotFired ?
                                <div className={styles.scheduledCard}>
                                    <div className={styles.scheduledLabel}>Scheduled for</div>
                                    <div className={styles.scheduledTime}>{formatUTCToIST(scheduledAt)}</div>
                                    <button
                                        type="button"
                                        className={styles.cancelBtn}
                                        onClick={cancelSchedule}
                                        disabled={isCancelling}
                                    >
                                        {isCancelling && <FaSpinner className={styles.btnSpinner} />}
                                        {isCancelling ? 'Cancelling...' : 'Cancel Scheduled Campaign'}
                                    </button>
                                </div>
                            : isLoadingAudience ?
                                <div style={{ padding: 20 }}>Loading audience data...</div>
                            :
                                <div>
                                    <BroadcastJobProgress
                                        audienceCount={audienceData?.length || 0}
                                        broadcastJob={broadcastJob}
                                        pollTimedOut={broadcastPollTimedOut}
                                        idleMessage={
                                            !hasTemplate
                                                ? 'Select a message template to continue.'
                                                : !hasAudience && !isLoadingAudience
                                                    ? 'Add an audience to continue.'
                                                    : 'Ready to launch.'
                                        }
                                    />
                                    {isSendAmbiguous && (
                                        <div className={styles.broadcastAmbiguous} style={{ marginTop: 14 }}>
                                            We're verifying whether this campaign went out — your balance is temporarily on hold. Check back in a few minutes, or contact support if this doesn't resolve.
                                        </div>
                                    )}
                                    <div className={styles.scheduleSection}>
                                        {!showSchedulePicker ? (
                                            <button
                                                type="button"
                                                className={styles.scheduleToggleBtn}
                                                onClick={() => setShowSchedulePicker(true)}
                                                disabled={isSendAmbiguous || isLoadingAudience || !hasTemplate || !hasAudience || isCurrentlyLaunching}
                                            >
                                                Schedule for later instead
                                            </button>
                                        ) : (
                                            <div className={styles.pickerCard}>
                                                <label className={styles.pickerLabel} htmlFor="scheduledAt">Send time (IST)</label>
                                                <input
                                                    id="scheduledAt"
                                                    type="datetime-local"
                                                    className={styles.pickerInput}
                                                    value={scheduledAtInput}
                                                    min={nowAsDatetimeLocal()}
                                                    onChange={(e) => setScheduledAtInput(e.target.value)}
                                                />
                                                <div className={styles.pickerHint}>All times are India Standard Time (IST).</div>
                                                <div className={styles.pickerActions}>
                                                    <button
                                                        type="button"
                                                        className={styles.pickerCancelBtn}
                                                        onClick={() => { setShowSchedulePicker(false); setScheduledAtInput('') }}
                                                        disabled={isScheduling}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={styles.scheduleBtn}
                                                        disabled={!scheduledAtInput || isScheduling || isCurrentlyLaunching}
                                                        onClick={scheduleCampaign}
                                                    >
                                                        {isScheduling && <FaSpinner className={styles.btnSpinner} />}
                                                        {isScheduling ? 'Scheduling...' : 'Schedule Campaign'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                        }
                    </div>
                    {/* The broadcast path's aggregate counts are already shown in the
                        progress panel to the left — a second raw-JSON dump of the same
                        numbers here was pure debug clutter, so it's only kept for the
                        legacy (USE_BROADCAST_SEND === false) path. */}
                    {!USE_BROADCAST_SEND && (
                        <div className={styles.analysisWrapper}>
                            <h3>Analysis Data</h3>
                            <pre>{JSON.stringify(analysisData, null, 2)}</pre>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CampaignLaunch;
